'use client';

/**
 * ConsumerUploadPanel Component
 *
 * Upload panel for the consumer portal data room with document type selector.
 * Used inside a SplitPanel to allow consumers to upload documents against a case,
 * with optional pre-filled document type (e.g., when uploading from a checklist
 * placeholder row).
 *
 * Pattern follows BulkUploadContent from the business portal (10-business-portal).
 */

import CheckCircle from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudUpload from '@mui/icons-material/CloudUpload';
import DocIcon from '@mui/icons-material/Description';
import {
  Box,
  GlassButton,
  GlassCard,
  GlassIconButton,
  GlassSelect,
  iconSizes,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SplitPanelContent,
  SplitPanelFooter,
  SplitPanelHeader,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPE_DISPLAY_NAMES,
  type DocumentCategory,
  type DocumentType,
} from '@ops-matrix/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

interface FileItem {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export interface ConsumerUploadPanelProps {
  /** Case ID to associate uploaded documents with */
  caseId: string;
  /** Pre-fill document type (e.g., when uploading from a checklist placeholder row) */
  defaultDocumentType?: string;
  /** Callback when panel is closed */
  onClose: () => void;
  /** Upload handler — receives files, document type, and category */
  onUpload: (files: File[], documentType: string | undefined, category: string | undefined) => Promise<void>;
  /** Whether an upload is currently in progress */
  isUploading?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ConsumerUploadPanel({
  defaultDocumentType,
  onClose,
  onUpload,
  isUploading = false,
}: ConsumerUploadPanelProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [documentType, setDocumentType] = useState<DocumentType | ''>((defaultDocumentType as DocumentType) || '');
  const [category, setCategory] = useState<DocumentCategory | ''>('');

  // Sync documentType when defaultDocumentType prop changes
  useEffect(() => {
    if (defaultDocumentType) setDocumentType(defaultDocumentType as DocumentType);
  }, [defaultDocumentType]);

  // -- File selection handlers ------------------------------------------------

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const newFiles: FileItem[] = Array.from(selectedFiles).map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      status: 'pending' as const,
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    // Reset the input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const droppedFiles = event.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const newFiles: FileItem[] = Array.from(droppedFiles).map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      status: 'pending' as const,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleRemoveFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // -- Upload handler ---------------------------------------------------------

  const handleUpload = useCallback(async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    // Mark all pending files as uploading
    setFiles((prev) => prev.map((f) => (f.status === 'pending' ? { ...f, status: 'uploading' as const } : f)));

    try {
      await onUpload(
        pendingFiles.map((f) => f.file),
        documentType || undefined,
        category || undefined,
      );

      // Mark all as success
      setFiles((prev) => prev.map((f) => (f.status === 'uploading' ? { ...f, status: 'success' as const } : f)));
    } catch {
      // Mark as error
      setFiles((prev) => prev.map((f) => (f.status === 'uploading' ? { ...f, status: 'error' as const } : f)));
    }
  }, [files, documentType, category, onUpload]);

  // -- Close handler ----------------------------------------------------------

  const handleClose = useCallback(() => {
    if (!isUploading) {
      setFiles([]);
      setDocumentType('');
      onClose();
    }
  }, [isUploading, onClose]);

  // -- Computed values --------------------------------------------------------

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const successCount = files.filter((f) => f.status === 'success').length;

  const documentTypeOptions = [
    { value: '', label: t('common.none') ?? 'None' },
    ...Object.entries(DOCUMENT_TYPE_DISPLAY_NAMES).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  // -- Render -----------------------------------------------------------------

  return (
    <>
      <SplitPanelHeader title={t('documents.upload') ?? 'Upload'} onClose={handleClose} />

      <SplitPanelContent>
        <Stack spacing={3}>
          {/* Document Type + Category Selectors */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <GlassSelect
              label={t('label.documentType') ?? 'Document Type'}
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as DocumentType | '')}
              options={documentTypeOptions}
              fullWidth
              size="small"
              sx={{ flex: 1 }}
            />
            <GlassSelect
              label={t('label.category') ?? 'Category'}
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentCategory | '')}
              options={[
                { value: '', label: t('common.none') ?? 'None' },
                ...DOCUMENT_CATEGORIES.map((cat) => ({ value: cat.value, label: cat.label })),
              ]}
              fullWidth
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Drop Zone */}
          <GlassCard
            hoverLift={false}
            sx={{
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              hidden
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.xls,.xlsx"
            />
            <CloudUpload sx={{ fontSize: iconSizes['3xl'], color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t('action.dropFilesHere') ?? 'Drop files here'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('message.orClickToSelect') ?? 'or click to select'}
            </Typography>
          </GlassCard>

          {/* File List */}
          {files.length > 0 && (
            <GlassCard hoverLift={false}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('label.selectedFiles') ?? 'Selected Files'} ({files.length})
              </Typography>
              <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                {files.map((fileItem) => (
                  <ListItem
                    key={fileItem.id}
                    secondaryAction={
                      fileItem.status === 'pending' && !isUploading ? (
                        <GlassIconButton size="small" onClick={() => handleRemoveFile(fileItem.id)}>
                          <CloseIcon fontSize="small" />
                        </GlassIconButton>
                      ) : undefined
                    }
                    sx={{
                      bgcolor:
                        fileItem.status === 'success'
                          ? 'success.main'
                          : fileItem.status === 'error'
                            ? 'error.main'
                            : 'transparent',
                      color: fileItem.status === 'success' || fileItem.status === 'error' ? 'white' : 'inherit',
                      borderRadius: 1,
                      mb: 0.5,
                      opacity: fileItem.status === 'success' ? 0.8 : 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {fileItem.status === 'success' ? (
                        <CheckCircle fontSize="small" sx={{ color: 'white' }} />
                      ) : (
                        <DocIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={fileItem.file.name}
                      secondary={
                        fileItem.status === 'uploading'
                          ? t('status.uploading')
                          : fileItem.status === 'error'
                            ? (fileItem.error ?? t('error.uploadFailed'))
                            : `${(fileItem.file.size / 1024).toFixed(0)} KB`
                      }
                      slotProps={{
                        primary: {
                          noWrap: true,
                          sx: { maxWidth: 280 },
                        },
                        secondary: {
                          sx: {
                            color:
                              fileItem.status === 'success' || fileItem.status === 'error'
                                ? 'inherit'
                                : 'text.secondary',
                            opacity: 0.9,
                          },
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              {isUploading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                </Box>
              )}
            </GlassCard>
          )}
        </Stack>
      </SplitPanelContent>

      <SplitPanelFooter>
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <GlassButton variant="outlined" fullWidth onClick={handleClose} disabled={isUploading}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            variant="gradient"
            fullWidth
            onClick={() => void handleUpload()}
            disabled={isUploading || pendingCount === 0}
            loading={isUploading}
          >
            {pendingCount > 0
              ? t('documents.uploadDialog.uploadFiles', {
                  count: pendingCount,
                })
              : successCount > 0
                ? t('common.done')
                : t('action.upload')}
          </GlassButton>
        </Box>
      </SplitPanelFooter>
    </>
  );
}

export default ConsumerUploadPanel;
